import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { ApiServerService } from '../../core/api';
import { ElasticFirestoreBase } from '../../core/db/elastic-firestore-base';
import { ElasticQueryOptions, ElasticSearchBody } from '../../core/db/elastic.models';
import { DiscountType } from '../../global/models';
import { OrderPaymentStatus } from '../payment';
import { Coupon, CouponMonthlyUsageBucket } from './coupon.models';

/**
 * Coupon Firestore data-access service.
 * Extends {@link ElasticFirestoreBase} to reuse generic CRUD and query helpers,
 * plus ElasticSearch-backed queries against related indices (e.g. `orders`).
 * Use inherited methods such as `get$`, `getOneWhere$`, `create`, `upsert`, and `update`
 * from higher layers (`coupon.service`) unless a coupon-specific query is required.
 */
@Injectable({ providedIn: 'root' })
export class CouponDbService extends ElasticFirestoreBase<Coupon> {
  constructor(firestore: Firestore, apiServerService: ApiServerService) {
    super(
      'coupons',
      firestore,
      apiServerService,
      { dateProperties: ['createdAt', 'updatedAt', 'startDate', 'expirationDate'] }
    );
  }

  /**
   * Get monthly usage counts of a coupon over a given range, computed from the
   * `orders` ElasticSearch index using a date_histogram aggregation.
   * @param couponCode Coupon code to filter by.
   * @param monthsBack Number of months back from now to include (defaults to 14).
   * @returns Array of `{ month: 'yyyy-MM', count }` ordered by most recent first.
   */
  async getMonthlyUsageByOrders(
    couponCode: string,
    monthsBack = 14
  ): Promise<CouponMonthlyUsageBucket[]> {
    const body: ElasticSearchBody = {
      size: 0,
      query: {
        bool: {
          filter: [
            { terms: { paymentStatus: [OrderPaymentStatus.PAID, OrderPaymentStatus.REFUNDED] } },
            {
              range: {
                'createdAt._seconds': {
                  gte: `now-${monthsBack}M/M`,
                  lte: 'now'
                }
              }
            },
            {
              nested: {
                path: 'cart.discounts',
                query: {
                  bool: {
                    filter: [
                      { term: { 'cart.discounts.type': DiscountType.COUPON } },
                      { term: { 'cart.discounts.refId': couponCode } }
                    ]
                  }
                }
              }
            } as any
          ]
        }
      },
      aggregations: {
        by_month: {
          date_histogram: {
            field: 'createdAt._seconds',
            calendar_interval: 'month',
            format: 'yyyy-MM',
            min_doc_count: 0,
            order: { _key: 'desc' },
          }
        } as any
      }
    };

    const options: ElasticQueryOptions<Coupon> = { index: 'orders' };
    const result = await this.search<Coupon>(body, options);
    const buckets: any[] = (result.aggregations as any)?.by_month?.buckets ?? [];

    return buckets.map(bucket => ({
      month: bucket.key_as_string as string,
      count: bucket.doc_count as number
    }));
  }
}
